import { Text, View } from '@components/primitives';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { regularFont } from '@theme/theme';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { changeAmountWithFormatting, numberWithDots } from '@utils/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from 'react-native';
import { getAmountInputStyle } from './styles';

interface Errors {
  [key: string]: any;
}

interface AmountForWithdrawalProps {
  handleChange: (field: string) => (value: string) => void;
  errors?: Errors;
  amount?: string;
  source?: string;
}

export const onFocusHandler =
  (source: string): (() => void) =>
  () => {
    const e = AMPLITUDE_CONSTANTS.transfer.transfer_manual_input;
    const { name, attributes } = e;
    AmplitudeHelper.logEvent(name, {
      [attributes.source]: source,
      [attributes.transaction_category_id]: 'wd-02',
    });
  };

const AmountForWithdrawal: React.FC<AmountForWithdrawalProps> = ({
  handleChange,
  errors = {},
  amount = '',
  source = '',
}) => {
  const { t } = useTranslation('common');
  const { tenantSettings } = getStore();
  const { currencySymbol, currencySeperatorRegEx } = tenantSettings;
  const [inputValue, setInputValue] = useState(amount);
  const [isFocused, setIsFocused] = useState(false);

  const changeAmountCallBack = (val: string) => {
    changeAmountWithFormatting(currencySeperatorRegEx, () => {}, setInputValue)(val);
    handleChange('amount')(val.toString().replace(/\./g, ''));
  };

  // Get the base style and add web-specific border fixes with focus state
  const baseStyle = getAmountInputStyle(errors);

  // Determine border color based on focus and error state
  const getBorderColor = () => {
    if (errors && errors.amount) {
      return colors.error.rose;
    }
    return isFocused ? colors.secondary.orchid : colors.neutral.darkMist;
  };

  const webStyle = {
    ...baseStyle,
    // Web-specific border fixes
    borderStyle: 'solid' as const,
    boxSizing: 'border-box' as any,
    // Dynamic border color based on focus state
    borderColor: getBorderColor(),
  };

  const textInputWebStyle = {
    fontFamily: regularFont,
    fontSize: scale(14),
    lineHeight: scale(21),
    height: scale(48),
    // Web-specific TextInput fixes
    borderWidth: 0,
    outline: 'none',
  };

  const containerStyle = { marginTop: 16 };
  const currencyTextStyle = { marginLeft: 16 };

  return (
    <View style={containerStyle}>
      <Text variant="sm-normal" color={colors.neutral.darkMist}>
        {t('bankTransfer.amountForTransfer')}
      </Text>
      <View style={webStyle}>
        <Text variant="md-normal" style={currencyTextStyle}>
          {currencySymbol}
        </Text>
        <TextInput
          keyboardType="numeric"
          testID="txtInputAmount"
          value={numberWithDots(inputValue)}
          onChangeText={changeAmountCallBack}
          onFocus={() => {
            setIsFocused(true);
            onFocusHandler(source)();
          }}
          onBlur={() => setIsFocused(false)}
          style={[appStyles.flex1, textInputWebStyle]}
        />
      </View>
    </View>
  );
};

export default AmountForWithdrawal;
